import type { Notification } from "@cloudserver/contracts";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { Bell } from "lucide-react";
import { DropdownMenu } from "radix-ui";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useMarkRead, useNotifications } from "../api/budgets.js";
import { money, relativeTime } from "../ui/format.js";

/*
 * The bell in the top bar (docs/budgets.md): the person's newest
 * notifications, unread ones first marked; the full list is `/notifications`.
 */

/** What a notification says, in the person's language, from its kind and data. */
export function notificationText(t: TFunction, n: Notification, locale: string): string {
  const d = n.data;
  const currency = typeof d.currency === "string" ? d.currency : "EUR";
  const fmt = (v: unknown) => money(String(v ?? 0), currency, locale);
  const project = n.projectName ?? "—";
  switch (n.kind) {
    case "budget.threshold":
      return t(`notifications.threshold.${d.basis === "forecast" ? "forecast" : "actual"}`, {
        project,
        threshold: d.threshold,
        percent: d.percent,
        amount: fmt(d.amount),
        budget: fmt(d.budget),
      });
    case "budget.raise_requested":
      return t("notifications.raiseRequested", {
        project,
        name: d.requestedBy ?? "—",
        amount: fmt(d.amount),
        kind: t(`budgets.kinds.${d.raiseKind === "permanent" ? "permanent" : "once"}`),
      });
    case "budget.raise_decided":
      return t(`notifications.raise.${d.decision === "approved" ? "approved" : "rejected"}`, {
        project,
        amount: fmt(d.amount),
      });
    case "approval.requested":
      return t("notifications.approvalRequested", {
        project,
        key: d.keyName ?? "—",
        action: d.action ?? "—",
        target: d.targetName ?? "—",
      });
    case "approval.decided": {
      const decision =
        d.decision === "executed" || d.decision === "failed" || d.decision === "expired"
          ? d.decision
          : "rejected";
      return t(`notifications.approvalDecided.${decision}`, {
        project,
        key: d.keyName ?? "—",
        action: d.action ?? "—",
      });
    }
  }
}

/** The approval request a notification is about (docs/approvals.md), if any. */
export const approvalOf = (n: Notification): string | null =>
  (n.kind === "approval.requested" || n.kind === "approval.decided") &&
  typeof n.data.approvalId === "string"
    ? n.data.approvalId
    : null;

/** Shown in the bell's list and on the notifications page. */
export function NotificationRow({ n }: { n: Notification }) {
  const { t, i18n } = useTranslation();
  return (
    <span className="flex min-w-0 items-start gap-2">
      <span
        className={clsx(
          "mt-1.5 size-2 shrink-0 rounded-full",
          n.readAt ? "bg-transparent" : "bg-accent",
        )}
        aria-hidden
      />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className={clsx("text-sm", !n.readAt && "font-medium")}>
          {notificationText(t, n, i18n.language)}
        </span>
        <span className="text-xs text-muted">
          {!n.readAt && <span className="sr-only">{t("notifications.unread")} · </span>}
          {relativeTime(n.createdAt, i18n.language)}
        </span>
      </span>
    </span>
  );
}

const SHOWN = 8;
const ITEM = "rounded-lg px-3 py-2 outline-none data-[highlighted]:bg-surface-2";

export function NotificationBell() {
  const { t } = useTranslation();
  const list = useNotifications();
  const read = useMarkRead();
  const unread = list.data?.unread ?? 0;
  const items = list.data?.items.slice(0, SHOWN) ?? [];
  const label =
    unread > 0 ? t("notifications.titleUnread", { count: unread }) : t("notifications.title");
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="relative rounded-lg p-2 text-muted hover:bg-surface-2 hover:text-text"
          aria-label={t("notifications.title")}
          title={label}
        >
          <Bell className="size-5" aria-hidden />
          {unread > 0 && (
            <span
              className="absolute right-0.5 top-0.5 flex min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[0.65rem] font-semibold leading-4 text-white"
              aria-hidden
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          collisionPadding={8}
          className="z-50 flex w-80 max-w-[calc(100vw-1rem)] flex-col rounded-xl border border-line bg-surface p-1 shadow-lg"
        >
          <DropdownMenu.Label className="px-3 py-2 text-sm font-semibold">
            {t("notifications.title")}
          </DropdownMenu.Label>
          {items.length === 0 ? (
            <p className="px-3 pb-3 text-sm text-muted">{t("notifications.empty")}</p>
          ) : (
            items.map((n) => (
              <DropdownMenu.Item key={n.id} asChild>
                {approvalOf(n) ? (
                  <Link
                    to="/api-access/approvals/$approvalId"
                    params={{ approvalId: approvalOf(n) ?? "" }}
                    onClick={() => !n.readAt && read.mutate([n.id])}
                    className={ITEM}
                  >
                    <NotificationRow n={n} />
                  </Link>
                ) : n.projectId ? (
                  <Link
                    to="/projects/$projectId/costs"
                    params={{ projectId: n.projectId }}
                    onClick={() => !n.readAt && read.mutate([n.id])}
                    className={ITEM}
                  >
                    <NotificationRow n={n} />
                  </Link>
                ) : (
                  <Link
                    to="/notifications"
                    onClick={() => !n.readAt && read.mutate([n.id])}
                    className={ITEM}
                  >
                    <NotificationRow n={n} />
                  </Link>
                )}
              </DropdownMenu.Item>
            ))
          )}
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <div className="flex items-center justify-between gap-2 px-1">
            {unread > 0 ? (
              <DropdownMenu.Item
                onSelect={() => read.mutate(undefined)}
                className="cursor-pointer rounded-lg px-2 py-2 text-sm text-accent outline-none data-[highlighted]:bg-surface-2"
              >
                {t("notifications.markAll")}
              </DropdownMenu.Item>
            ) : (
              <span />
            )}
            <DropdownMenu.Item asChild>
              <Link
                to="/notifications"
                className="whitespace-nowrap rounded-lg px-2 py-2 text-sm text-accent outline-none data-[highlighted]:bg-surface-2"
              >
                {t("notifications.all")}
              </Link>
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
